import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptInvitation,
  changeCollaboratorRole,
  declineInvitation,
  inviteCollaborator,
  listAuditLog,
  listCollaborators,
  listMyInvitations,
  revokeCollaborator,
} from '@/api/collaboration'
import type { CreateCollaboratorRequest, UpdateCollaboratorRoleRequest } from '@/api/types'

const COLLABORATORS_KEY = (projectId: string) => ['collaborators', projectId] as const
const INVITATIONS_KEY = ['my-invitations'] as const
const AUDIT_LOG_KEY = (projectId: string, page: number) => ['audit-log', projectId, page] as const

export const useCollaborators = (projectId: string) =>
  useQuery({
    queryKey: COLLABORATORS_KEY(projectId),
    queryFn: () => listCollaborators(projectId),
  })

export const useMyInvitations = () =>
  useQuery({
    queryKey: INVITATIONS_KEY,
    queryFn: listMyInvitations,
    // Pending invitations are high-signal; let TanStack refetch on focus.
    staleTime: 30 * 1000,
  })

export const useAuditLog = (projectId: string, page: number = 0) =>
  useQuery({
    queryKey: AUDIT_LOG_KEY(projectId, page),
    queryFn: () => listAuditLog(projectId, page),
  })

export const useInviteCollaborator = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: CreateCollaboratorRequest) => inviteCollaborator(projectId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: COLLABORATORS_KEY(projectId) }),
  })
}

export const useChangeCollaboratorRole = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, request }: { userId: string; request: UpdateCollaboratorRoleRequest }) =>
      changeCollaboratorRole(projectId, userId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: COLLABORATORS_KEY(projectId) }),
  })
}

export const useRevokeCollaborator = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => revokeCollaborator(projectId, userId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: COLLABORATORS_KEY(projectId) })
      qc.invalidateQueries({ queryKey: ['projects', 'detail', projectId] })
    },
  })
}

export const useAcceptInvitation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => acceptInvitation(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITATIONS_KEY })
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

export const useDeclineInvitation = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (projectId: string) => declineInvitation(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: INVITATIONS_KEY })
      qc.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
